import { useState, useEffect } from 'react'
import { Save, AlertCircle, Upload, Palette, Eye, FileText, Globe, Phone, Plus, Trash2 } from 'lucide-react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import catalogoOnlineConfigService from '../../services/catalogoOnlineConfigService'

export default function CatalogoOnlineConfig() {
  const { organizadorAtivo } = useOrganizador()

  // Estado da seção de visibilidade + cores do catálogo + whatsapp
  const [config, setConfig] = useState({
    exibir_observacoes: true,
    exibir_fazenda: true,
    exibir_peso_quantidade: true,
    exibir_status_vendido: false,
    exibir_valor_minimo: false,
    exibir_totais_sexo: true,
    modo_peso: 'peso_arroba',
    cor_header: '#1E40AF',
    cor_fundo: '#F5F5F5',
    cor_botao_interesse: '#25D366',
    whatsapp_contatos: []
  })

  // Estado da seção de regulamento
  const [regulamento, setRegulamento] = useState({
    tipo: 'texto',
    texto: '',
    pdf_url: ''
  })
  const [pdfFile, setPdfFile] = useState(null)

  // Estado da seção de cores do organizador
  const [cores, setCores] = useState({
    cor_primaria: '#1E40AF',
    cor_secundaria: '#F59E0B'
  })

  // Estado geral
  const [loading, setLoading] = useState(false)
  const [loadingRegulamento, setLoadingRegulamento] = useState(false)
  const [loadingCores, setLoadingCores] = useState(false)
  const [loadingExtrair, setLoadingExtrair] = useState(false)
  const [message, setMessage] = useState('')
  const [messageRegulamento, setMessageRegulamento] = useState('')
  const [messageCores, setMessageCores] = useState('')

  useEffect(() => {
    if (organizadorAtivo?.id) {
      loadConfig()
      loadRegulamento()
    }
  }, [organizadorAtivo])

  const loadConfig = async () => {
    try {
      const response = await catalogoOnlineConfigService.getConfig(organizadorAtivo.id)
      setConfig({
        exibir_observacoes: response.exibir_observacoes ?? true,
        exibir_fazenda: response.exibir_fazenda ?? true,
        exibir_peso_quantidade: response.exibir_peso_quantidade ?? true,
        exibir_status_vendido: response.exibir_status_vendido ?? false,
        exibir_valor_minimo: response.exibir_valor_minimo ?? false,
        exibir_totais_sexo: response.exibir_totais_sexo ?? true,
        modo_peso: response.modo_peso || 'peso_arroba',
        cor_header: response.cor_header || '#1E40AF',
        cor_fundo: response.cor_fundo || '#F5F5F5',
        cor_botao_interesse: response.cor_botao_interesse || '#25D366',
        whatsapp_contatos: response.whatsapp_contatos || []
      })
    } catch (error) {
      console.error('Erro ao carregar configuração:', error)
    }
  }

  const loadRegulamento = async () => {
    try {
      const response = await catalogoOnlineConfigService.getRegulamento(organizadorAtivo.id)
      setRegulamento({
        tipo: response.regulamento_tipo || 'texto',
        texto: response.regulamento_texto || '',
        pdf_url: response.regulamento_pdf_url || ''
      })
    } catch (error) {
      console.error('Erro ao carregar regulamento:', error)
    }
  }

  // Salvar configuração completa (visibilidade + cores catálogo + whatsapp)
  const handleSaveConfig = async () => {
    try {
      setLoading(true)
      await catalogoOnlineConfigService.saveConfig(organizadorAtivo.id, config)
      setMessage('Configurações salvas com sucesso!')
      setTimeout(() => setMessage(''), 3000)
    } catch (error) {
      console.error('Erro ao salvar configurações:', error)
      setMessage('Erro ao salvar configurações')
      setTimeout(() => setMessage(''), 3000)
    } finally {
      setLoading(false)
    }
  }

  // Salvar regulamento
  const handleSaveRegulamento = async () => {
    try {
      setLoadingRegulamento(true)
      const formData = new FormData()
      formData.append('regulamento_tipo', regulamento.tipo)

      if (regulamento.tipo === 'texto') {
        formData.append('regulamento_texto', regulamento.texto)
      } else if (regulamento.tipo === 'pdf' && pdfFile) {
        if (pdfFile.size > 10 * 1024 * 1024) {
          setMessageRegulamento('Arquivo excede o limite de 10MB')
          setTimeout(() => setMessageRegulamento(''), 3000)
          return
        }
        formData.append('arquivo', pdfFile)
      }

      await catalogoOnlineConfigService.saveRegulamento(organizadorAtivo.id, formData)
      setMessageRegulamento('Regulamento salvo com sucesso!')
      setPdfFile(null)
      setTimeout(() => setMessageRegulamento(''), 3000)
    } catch (error) {
      const msg = error.response?.status === 413
        ? 'Arquivo excede o limite de 10MB'
        : 'Erro ao salvar regulamento'
      setMessageRegulamento(msg)
      setTimeout(() => setMessageRegulamento(''), 3000)
    } finally {
      setLoadingRegulamento(false)
    }
  }

  // Salvar cores do organizador
  const handleSaveCores = async () => {
    try {
      setLoadingCores(true)
      await catalogoOnlineConfigService.saveCores(organizadorAtivo.id, cores)
      setMessageCores('Cores salvas com sucesso!')
      setTimeout(() => setMessageCores(''), 3000)
    } catch (error) {
      setMessageCores('Erro ao salvar cores')
      setTimeout(() => setMessageCores(''), 3000)
    } finally {
      setLoadingCores(false)
    }
  }

  // Extrair cores do logo
  const handleExtrairCores = async () => {
    try {
      setLoadingExtrair(true)
      const response = await catalogoOnlineConfigService.extrairCores(organizadorAtivo.id)
      if (response.cores_extraidas) {
        setCores({
          cor_primaria: response.cores_extraidas.primaria || '#1E40AF',
          cor_secundaria: response.cores_extraidas.secundaria || '#F59E0B'
        })
        setMessageCores('Cores extraídas do logo com sucesso!')
      }
      setTimeout(() => setMessageCores(''), 3000)
    } catch (error) {
      setMessageCores('Erro ao extrair cores do logo')
      setTimeout(() => setMessageCores(''), 3000)
    } finally {
      setLoadingExtrair(false)
    }
  }

  // WhatsApp contatos
  const addContato = () => {
    setConfig(prev => ({
      ...prev,
      whatsapp_contatos: [...prev.whatsapp_contatos, { nome: '', numero: '' }]
    }))
  }

  const removeContato = (idx) => {
    setConfig(prev => ({
      ...prev,
      whatsapp_contatos: prev.whatsapp_contatos.filter((_, i) => i !== idx)
    }))
  }

  const updateContato = (idx, field, value) => {
    setConfig(prev => ({
      ...prev,
      whatsapp_contatos: prev.whatsapp_contatos.map((c, i) =>
        i === idx ? { ...c, [field]: value } : c
      )
    }))
  }

  const handlePdfChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setMessageRegulamento('Arquivo excede o limite de 10MB')
        setTimeout(() => setMessageRegulamento(''), 3000)
        return
      }
      setPdfFile(file)
    }
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-700 rounded-xl px-4 sm:px-6 py-4 sm:py-6">
        <div className="flex items-center gap-3">
          <Globe className="w-7 h-7 text-white" />
          <div>
            <h1 className="text-xl sm:text-3xl font-bold text-white">Catálogo Online</h1>
            <p className="text-teal-100 mt-1 text-sm sm:text-base">Configure a página pública do catálogo online</p>
          </div>
        </div>
      </div>

      {/* Seção 1: Visibilidade */}
      <div className="bg-white shadow-sm rounded-xl border border-gray-100">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <Eye className="w-5 h-5 text-teal-600" />
            <h2 className="text-lg font-semibold text-gray-900">Visibilidade de Campos</h2>
          </div>

          <div className="space-y-4">
            {[
              { key: 'exibir_observacoes', label: 'Exibir observações', desc: 'Mostra o campo de observações de cada lote' },
              { key: 'exibir_fazenda', label: 'Exibir fazenda do vendedor', desc: 'Mostra o nome da fazenda do vendedor em cada lote' },
              { key: 'exibir_peso_quantidade', label: 'Exibir peso/quantidade', desc: 'Mostra o peso total e quantidade de animais do lote' },
              { key: 'exibir_status_vendido', label: 'Exibir status "Vendido"', desc: 'Mostra badge "Vendido" nos lotes já faturados' },
              { key: 'exibir_valor_minimo', label: 'Exibir valor mínimo', desc: 'Mostra o valor mínimo do lote ao lado do botão "Tenho Interesse"' },
              { key: 'exibir_totais_sexo', label: 'Exibir totais por sexo', desc: 'Mostra a linha com totais de Machos/Fêmeas/Castrados no topo do catálogo' },
            ].map(item => (
              <label key={item.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <span className="text-sm font-medium text-gray-700">{item.label}</span>
                  <p className="text-xs text-gray-500">{item.desc}</p>
                </div>
                <input
                  type="checkbox"
                  checked={config[item.key]}
                  onChange={(e) => setConfig(prev => ({ ...prev, [item.key]: e.target.checked }))}
                  className="rounded border-gray-300 text-teal-600 focus:ring-teal-500 w-5 h-5"
                />
              </label>
            ))}

            {/* Modo Peso */}
            {config.exibir_peso_quantidade && (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <span className="text-sm font-medium text-gray-700">Exibição do Peso</span>
                  <p className="text-xs text-gray-500">Como o peso aparece no catálogo online</p>
                </div>
                <div className="flex rounded-lg overflow-hidden border border-gray-300 w-48">
                  <button
                    type="button"
                    onClick={() => setConfig(prev => ({ ...prev, modo_peso: 'peso' }))}
                    className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors ${config.modo_peso === 'peso' ? 'bg-teal-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                  >
                    Só Peso
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfig(prev => ({ ...prev, modo_peso: 'peso_arroba' }))}
                    className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors ${(config.modo_peso || 'peso_arroba') === 'peso_arroba' ? 'bg-teal-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                  >
                    Peso + @
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Seção 2: Cores do Catálogo */}
      <div className="bg-white shadow-sm rounded-xl border border-gray-100">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <Palette className="w-5 h-5 text-teal-600" />
            <h2 className="text-lg font-semibold text-gray-900">Cores do Catálogo</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <ColorPicker label="Cor do Header" value={config.cor_header} onChange={(v) => setConfig(prev => ({ ...prev, cor_header: v }))} />
            <ColorPicker label="Cor de Fundo" value={config.cor_fundo} onChange={(v) => setConfig(prev => ({ ...prev, cor_fundo: v }))} />
            <ColorPicker label="Cor Botão Interesse" value={config.cor_botao_interesse} onChange={(v) => setConfig(prev => ({ ...prev, cor_botao_interesse: v }))} />
          </div>

          {/* Preview */}
          <div className="mt-6">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Preview</h4>
            <div className="rounded-lg overflow-hidden border border-gray-200">
              <div className="h-14 flex items-center px-4" style={{ backgroundColor: config.cor_header }}>
                <span className="text-white font-semibold text-sm">Cabeçalho do Catálogo</span>
              </div>
              <div className="p-4" style={{ backgroundColor: config.cor_fundo }}>
                <button className="px-4 py-2 rounded-lg text-white text-sm font-medium" style={{ backgroundColor: config.cor_botao_interesse }}>
                  Tenho Interesse!
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Seção 3: Contatos WhatsApp */}
      <div className="bg-white shadow-sm rounded-xl border border-gray-100">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <Phone className="w-5 h-5 text-teal-600" />
            <h2 className="text-lg font-semibold text-gray-900">Contatos WhatsApp</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Configure os números de WhatsApp para o botão "Tenho Interesse". Se houver mais de um, o visitante escolhe para quem enviar.
          </p>

          <div className="space-y-3">
            {config.whatsapp_contatos.map((contato, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <input
                  type="text"
                  value={contato.nome}
                  onChange={(e) => updateContato(idx, 'nome', e.target.value)}
                  placeholder="Nome (ex: João)"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-teal-500 focus:border-teal-500"
                />
                <input
                  type="text"
                  value={contato.numero}
                  onChange={(e) => updateContato(idx, 'numero', e.target.value)}
                  placeholder="Número (ex: 5534999999999)"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-teal-500 focus:border-teal-500"
                />
                <button
                  onClick={() => removeContato(idx)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <button
            onClick={addContato}
            className="mt-3 flex items-center gap-2 px-4 py-2 text-sm font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Adicionar Contato
          </button>
        </div>
      </div>

      {/* Botão Salvar (visibilidade + cores catálogo + whatsapp) */}
      <div className="bg-white shadow-sm rounded-xl border border-gray-100 p-6">
        <div className="flex items-center justify-between">
          <div>
            {message && (
              <span className={`text-sm ${message.includes('sucesso') ? 'text-green-600' : 'text-red-600'}`}>
                {message}
              </span>
            )}
          </div>
          <button
            onClick={handleSaveConfig}
            disabled={loading}
            className="flex items-center px-5 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 font-medium"
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Salvando...' : 'Salvar Configurações'}
          </button>
        </div>
      </div>

      {/* Seção 4: Regulamento */}
      <div className="bg-white shadow-sm rounded-xl border border-gray-100">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="w-5 h-5 text-teal-600" />
            <h2 className="text-lg font-semibold text-gray-900">Regulamento</h2>
          </div>

          {/* Toggle tipo */}
          <div className="flex gap-4 mb-4">
            <button
              onClick={() => setRegulamento(prev => ({ ...prev, tipo: 'texto' }))}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                regulamento.tipo === 'texto' ? 'bg-teal-100 text-teal-700 border border-teal-300' : 'bg-gray-100 text-gray-600 border border-gray-200'
              }`}
            >
              Texto Rico
            </button>
            <button
              onClick={() => setRegulamento(prev => ({ ...prev, tipo: 'pdf' }))}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                regulamento.tipo === 'pdf' ? 'bg-teal-100 text-teal-700 border border-teal-300' : 'bg-gray-100 text-gray-600 border border-gray-200'
              }`}
            >
              Upload PDF
            </button>
          </div>

          {regulamento.tipo === 'texto' ? (
            <textarea
              value={regulamento.texto}
              onChange={(e) => setRegulamento(prev => ({ ...prev, texto: e.target.value }))}
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-teal-500 focus:border-teal-500"
              placeholder="Digite o regulamento do leilão aqui..."
            />
          ) : (
            <div>
              <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors w-fit">
                <Upload className="w-4 h-4 text-gray-600" />
                <span className="text-sm text-gray-700">{pdfFile ? pdfFile.name : 'Selecionar PDF'}</span>
                <input type="file" accept=".pdf" onChange={handlePdfChange} className="hidden" />
              </label>
              {regulamento.pdf_url && !pdfFile && (
                <p className="text-xs text-green-600 mt-2">✓ PDF atual cadastrado</p>
              )}
            </div>
          )}

          <div className="flex items-center justify-between pt-6 mt-6 border-t border-gray-200">
            <div>
              {messageRegulamento && (
                <span className={`text-sm ${messageRegulamento.includes('sucesso') ? 'text-green-600' : 'text-red-600'}`}>
                  {messageRegulamento}
                </span>
              )}
            </div>
            <button onClick={handleSaveRegulamento} disabled={loadingRegulamento} className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50">
              <Save className="w-4 h-4 mr-2" />
              {loadingRegulamento ? 'Salvando...' : 'Salvar Regulamento'}
            </button>
          </div>
        </div>
      </div>

      {/* Seção 5: Cores do Organizador (primária/secundária) */}
      <div className="bg-white shadow-sm rounded-xl border border-gray-100">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <Palette className="w-5 h-5 text-teal-600" />
            <h2 className="text-lg font-semibold text-gray-900">Cores do Organizador</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">Cores da barra de cada lote e identidade visual geral.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ColorPicker label="Cor Primária (barra do lote)" value={cores.cor_primaria} onChange={(v) => setCores(prev => ({ ...prev, cor_primaria: v }))} />
            <ColorPicker label="Cor Secundária" value={cores.cor_secundaria} onChange={(v) => setCores(prev => ({ ...prev, cor_secundaria: v }))} />
          </div>

          <div className="flex items-center justify-between pt-6 mt-6 border-t border-gray-200">
            <div>
              {messageCores && (
                <span className={`text-sm ${messageCores.includes('sucesso') ? 'text-green-600' : 'text-red-600'}`}>
                  {messageCores}
                </span>
              )}
            </div>
            <button onClick={handleSaveCores} disabled={loadingCores} className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50">
              <Save className="w-4 h-4 mr-2" />
              {loadingCores ? 'Salvando...' : 'Salvar Cores'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function ColorPicker({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <div className="flex items-center gap-3">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-10 rounded-lg border border-gray-300 cursor-pointer"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-28 uppercase"
          maxLength={7}
        />
      </div>
    </div>
  )
}
